import {type MutableRefObject, useCallback, useEffect, useRef} from 'react';
import * as signalR from '@microsoft/signalr';
import {useAssistantIntegration} from '../AssistantIntegration';
import {AssistantEventEnvelope} from '../api/types';
import {
    type SignalRConnectionState,
    type SignalRGroupSubscription,
    useSignalRGroupHub
} from './useSignalRGroupHub';

export interface UseAssistantEventHubResult {
    connectionState: SignalRConnectionState;
    isJoined: boolean;
    waitUntilJoined: () => Promise<void>;
    error: Error | null;
}

interface AssistantEventCallbacks {
    onEvent: (event: AssistantEventEnvelope) => void;
    onCatchUpNeeded?: () => void | Promise<void>;
}

/**
 * Joins the thread's SignalR group and streams its events. Connects lazily (only when a
 * threadId is provided), rejoins automatically after reconnects and triggers a state catch-up
 * (snapshot refetch) whenever events may have been missed.
 */
export function useAssistantEventHub(
    threadId: string | undefined,
    onEvent: (event: AssistantEventEnvelope) => void,
    onCatchUpNeeded?: () => void | Promise<void>
): UseAssistantEventHubResult {
    const {accessTokenFactory, baseUrl} = useAssistantIntegration();
    const onEventRef = useRef(onEvent);
    const onCatchUpNeededRef = useRef(onCatchUpNeeded);

    useEffect(() => {
        onEventRef.current = onEvent;
    }, [onEvent]);

    useEffect(() => {
        onCatchUpNeededRef.current = onCatchUpNeeded;
    }, [onCatchUpNeeded]);

    const registerHandlers = useCallback(
        (
            connection: signalR.HubConnection,
            subscriptionsRef: MutableRefObject<
                Map<string, SignalRGroupSubscription<AssistantEventCallbacks>>
            >
        ) => {
            connection.on(
                'ReceiveThreadEvent',
                (event: AssistantEventEnvelope) => {
                    const subscription = subscriptionsRef.current.get(
                        event.threadId
                    );

                    subscription?.callbacks.onEvent(event);
                }
            );
        },
        []
    );

    const {
        connectionState,
        error,
        subscribeToGroup,
        waitUntilJoined: waitUntilGroupJoined,
        isGroupJoined
    } = useSignalRGroupHub<AssistantEventCallbacks>({
        hubName: 'assistant thread hub',
        hubUrl: `${baseUrl}/hubs/assistant-threads`,
        enabled: !!threadId,
        accessTokenFactory,
        withCredentials: true,
        joinMethod: 'JoinThread',
        leaveMethod: 'LeaveThread',
        registerHandlers,
        onCatchUpNeeded: (_threadId, callbacks) => callbacks.onCatchUpNeeded?.()
    });

    useEffect(() => {
        if (!threadId) {
            return;
        }

        return subscribeToGroup(threadId, {
            onEvent: event => onEventRef.current(event),
            onCatchUpNeeded: () => onCatchUpNeededRef.current?.()
        });
    }, [subscribeToGroup, threadId]);

    const waitUntilJoined = useCallback(async () => {
        if (!threadId) {
            throw new Error('The assistant live connection is not ready.');
        }

        await waitUntilGroupJoined(threadId);
    }, [threadId, waitUntilGroupJoined]);

    return {
        connectionState,
        isJoined: threadId ? isGroupJoined(threadId) : false,
        waitUntilJoined,
        error
    };
}
